import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Affix,
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Rate,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  ExportOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  LinkOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../config/api';

dayjs.extend(isoWeek);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

type Frequency = 'weekly' | 'biweekly' | 'monthly';
type AuditTab = 'dashboard' | 'new-audit' | 'audit-reports';
const VALID_TABS = new Set<AuditTab>(['dashboard', 'new-audit', 'audit-reports']);

function defaultPeriodForFrequency(frequency: Frequency): [Dayjs, Dayjs] {
  if (frequency === 'monthly') {
    const start = dayjs().subtract(1, 'month').startOf('month');
    const end = dayjs().subtract(1, 'month').endOf('month');
    return [start, end];
  }
  if (frequency === 'biweekly') {
    const end = dayjs().subtract(1, 'day').startOf('day');
    const start = end.subtract(13, 'day').startOf('day');
    return [start, end];
  }
  const start = dayjs().startOf('isoWeek').subtract(1, 'week');
  const end = start.endOf('isoWeek');
  return [start, end];
}

function normalizeSupervisorScore(score: number | undefined): number | null {
  if (score == null || score === 0 || Number.isNaN(score)) return null;
  if (score < 1 || score > 5) return null;
  return score;
}

function formatResolveDuration(createdAt?: string, resolvedAt?: string | null): string {
  if (!createdAt) return '—';
  if (!resolvedAt) return 'Not solved yet (no resolved date)';
  const a = new Date(createdAt).getTime();
  const b = new Date(resolvedAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return '—';
  const ms = b - a;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 48) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatPeriodDate(v: string | undefined): string {
  if (!v) return '—';
  return dayjs(v).format('YYYY-MM-DD');
}

interface AuditableUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  last_audit_at?: string | null;
  last_period_start?: string | null;
  last_period_end?: string | null;
}

interface SampleTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  updated_at?: string;
  business_name?: string;
  feedback_has_row?: boolean;
  feedback_is_submitted?: boolean;
  feedback_rating?: number | null;
  feedback_comment?: string | null;
  feedback_submitted_at?: string | null;
}

interface GradeRow {
  strengths: string;
  improvements: string;
  score: number | undefined;
}

interface AuditConfig {
  frequency: Frequency;
  pool_size: number;
  selection_size: number;
  sample_size: number;
  target_roles: string[];
}

const TicketAudit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const tabParam = new URLSearchParams(location.search).get('tab');
  const resolvedTab = tabParam === 'recent-reports' ? 'audit-reports' : tabParam;
  const activeTab: AuditTab =
    resolvedTab && VALID_TABS.has(resolvedTab as AuditTab) ? (resolvedTab as AuditTab) : 'dashboard';

  const [agentId, setAgentId] = useState<string | undefined>();
  const [period, setPeriod] = useState<[Dayjs, Dayjs] | null>(null);
  const [candidates, setCandidates] = useState<SampleTicket[]>([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
  const [grades, setGrades] = useState<Record<string, GradeRow>>({});
  const [summaryNotes, setSummaryNotes] = useState('');
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailReport, setDetailReport] = useState<{ report: any; items: any[] } | null>(null);
  const [lastSavedReportId, setLastSavedReportId] = useState<string | null>(null);
  const [saveBannerVisible, setSaveBannerVisible] = useState(false);
  const [highlightReportId, setHighlightReportId] = useState<string | null>(null);
  const [expandedReportItems, setExpandedReportItems] = useState<Record<string, any[]>>({});
  const [expandingReportId, setExpandingReportId] = useState<string | null>(null);

  const [previewTicketId, setPreviewTicketId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTicket, setPreviewTicket] = useState<any>(null);
  const [previewComments, setPreviewComments] = useState<any[]>([]);
  const [previewFields, setPreviewFields] = useState<any[]>([]);

  const [exportBusy, setExportBusy] = useState<string | null>(null);

  const [reportsAgentFilter, setReportsAgentFilter] = useState<string | undefined>();
  const [reportsDateRange, setReportsDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [reportsPage, setReportsPage] = useState(1);
  const reportsPageSize = 20;

  const [analyticsRange, setAnalyticsRange] = useState<[Dayjs, Dayjs] | null>(() => {
    const end = dayjs();
    const start = end.subtract(89, 'day');
    return [start, end];
  });

  const handleTabChange = (key: string) => {
    if (key === 'dashboard') {
      navigate('/ticket-audit');
    } else {
      navigate(`/ticket-audit?tab=${key}`);
    }
  };

  const { data: config, isLoading: configLoading } = useQuery(
    'ticket-audit-config',
    async () => {
      const res = await api.get('/api/ticket-audits/config');
      return res.data as AuditConfig;
    }
  );

  const { data: auditableData, isLoading: auditableLoading } = useQuery(
    'ticket-audit-auditable-users',
    async () => {
      const res = await api.get('/api/ticket-audits/auditable-users');
      return res.data as { users: AuditableUser[] };
    }
  );

  const auditableUsers = useMemo(() => auditableData?.users ?? [], [auditableData]);
  const poolSize = config?.pool_size ?? 15;
  const selectionSize = config?.selection_size ?? config?.sample_size ?? 5;
  const frequency = (config?.frequency || 'weekly') as Frequency;

  useEffect(() => {
    if (!configLoading && config && !period) {
      setPeriod(defaultPeriodForFrequency(frequency));
    }
  }, [configLoading, config, frequency, period]);

  useEffect(() => {
    if (highlightReportId) {
      const t = setTimeout(() => setHighlightReportId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [highlightReportId]);

  const applyFrequencyDefaults = () => {
    setPeriod(defaultPeriodForFrequency(frequency));
    message.info('Period reset to match the configured cadence');
  };

  const analyticsFrom = analyticsRange?.[0]?.format('YYYY-MM-DD');
  const analyticsTo = analyticsRange?.[1]?.format('YYYY-MM-DD');

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery(
    ['ticket-audit-analytics', analyticsFrom, analyticsTo],
    async () => {
      const res = await api.get('/api/ticket-audits/analytics', {
        params: { from: analyticsFrom, to: analyticsTo },
      });
      return res.data as {
        by_agent: Array<{
          agent_id: string;
          agent_first_name: string;
          agent_last_name: string;
          avg_score: string | number | null;
          item_count: number;
          report_count: number;
        }>;
        by_month: Array<{ month_key: string; report_count: number; avg_score: string | number | null }>;
      };
    },
    { enabled: activeTab === 'dashboard' && !!analyticsFrom && !!analyticsTo }
  );

  const reportsFrom = reportsDateRange?.[0]?.format('YYYY-MM-DD');
  const reportsTo = reportsDateRange?.[1]?.format('YYYY-MM-DD');

  const {
    data: reportsData,
    isLoading: reportsLoading,
    isError: reportsError,
    error: reportsQueryError,
    refetch: refetchReports,
  } = useQuery(
    ['ticket-audit-reports', reportsPage, reportsAgentFilter, reportsFrom, reportsTo],
    async () => {
      const params: Record<string, string | number> = {
        page: reportsPage,
        limit: reportsPageSize,
      };
      if (reportsAgentFilter) params.agent_id = reportsAgentFilter;
      if (reportsFrom) params.from = reportsFrom;
      if (reportsTo) params.to = reportsTo;
      const res = await api.get('/api/ticket-audits/reports', { params });
      return res.data as { reports: any[]; total: number };
    },
    { enabled: activeTab === 'audit-reports', refetchOnMount: 'always' }
  );

  const barChartData = useMemo(() => {
    return (analyticsData?.by_agent || []).map((r) => ({
      name: `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim() || r.agent_id,
      avgScore: r.avg_score != null ? Number(r.avg_score) : null,
      reports: r.report_count,
      items: r.item_count,
    }));
  }, [analyticsData]);

  const lineChartData = useMemo(() => {
    return (analyticsData?.by_month || []).map((r) => ({
      month: r.month_key,
      reports: r.report_count,
      avgScore: r.avg_score != null ? Number(r.avg_score) : null,
    }));
  }, [analyticsData]);

  const selectedTickets = useMemo(
    () => candidates.filter((t) => selectedTicketIds.includes(t.id)),
    [candidates, selectedTicketIds]
  );

  const toggleTicketSelection = (ticketId: string, checked: boolean) => {
    setSelectedTicketIds((prev) => {
      if (checked) {
        if (prev.includes(ticketId)) return prev;
        if (prev.length >= selectionSize) {
          message.warning(`You can select at most ${selectionSize} tickets`);
          return prev;
        }
        return [...prev, ticketId];
      }
      return prev.filter((id) => id !== ticketId);
    });
  };

  const loadCandidates = async () => {
    if (!agentId || !period?.[0] || !period[1]) {
      message.warning('Select an agent and audit period');
      return;
    }
    setLoadingCandidates(true);
    try {
      const res = await api.post('/api/ticket-audits/sample', {
        agent_id: agentId,
        period_start: period[0].format('YYYY-MM-DD'),
        period_end: period[1].format('YYYY-MM-DD'),
      });
      const tickets: SampleTicket[] = res.data.tickets || [];
      const limit = res.data.pool_size ?? res.data.requested_limit ?? poolSize;
      setCandidates(tickets);
      setSelectedTicketIds([]);
      setGrades({});
      setSaveBannerVisible(false);
      setLastSavedReportId(null);
      if (tickets.length < limit) {
        message.warning(
          `Only ${tickets.length} ticket(s) matched this agent and period (need activity in range, status other than New).`
        );
      } else {
        message.success(`Loaded ${tickets.length} recent tickets — select ${selectionSize} to audit`);
      }
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load tickets');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const openDetail = useCallback(async (id: string) => {
    try {
      const res = await api.get(`/api/ticket-audits/reports/${id}`);
      setDetailReport(res.data);
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load report');
    }
  }, []);

  const saveReport = async () => {
    if (!agentId || !period?.[0] || !period[1]) {
      message.warning('Select an agent and period');
      return;
    }
    if (selectedTicketIds.length !== selectionSize) {
      message.warning(`Select exactly ${selectionSize} tickets to audit`);
      return;
    }
    const emptyNotes = selectedTickets.filter((t) => {
      const g = grades[t.id] || { strengths: '', improvements: '', score: undefined };
      return !g.strengths?.trim() && !g.improvements?.trim();
    });
    if (emptyNotes.length > 0) {
      message.warning(
        `${emptyNotes.length} selected ticket(s) have no strengths or improvements noted. Consider adding feedback before saving.`
      );
    }
    const items = selectedTickets.map((t) => {
      const g = grades[t.id] || { strengths: '', improvements: '', score: undefined };
      return {
        ticket_id: t.id,
        strengths: g.strengths || null,
        improvements: g.improvements || null,
        score: normalizeSupervisorScore(g.score),
      };
    });
    setSaving(true);
    try {
      const res = await api.post('/api/ticket-audits/reports', {
        agent_id: agentId,
        period_start: period[0].format('YYYY-MM-DD'),
        period_end: period[1].format('YYYY-MM-DD'),
        summary_notes: summaryNotes || null,
        items,
      });
      const id = res.data?.id as string | undefined;
      setLastSavedReportId(id || null);
      setSaveBannerVisible(true);
      setHighlightReportId(id || null);
      message.success('Audit report saved');
      setCandidates([]);
      setSelectedTicketIds([]);
      setGrades({});
      setSummaryNotes('');
      navigate('/ticket-audit?tab=audit-reports');
      await queryClient.refetchQueries('ticket-audit-reports');
      queryClient.invalidateQueries('ticket-audit-auditable-users');
      queryClient.invalidateQueries('ticket-audit-analytics');
      if (id) {
        await openDetail(id);
      }
    } catch (e: any) {
      const err = e.response?.data?.error || e.message || 'Failed to save report';
      message.error(typeof err === 'string' ? err : 'Failed to save report');
      console.error('[TicketAudit] save failed', e.response?.status, e.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const openTicketPreview = async (ticketId: string) => {
    setPreviewTicketId(ticketId);
    setPreviewLoading(true);
    setPreviewTicket(null);
    setPreviewComments([]);
    setPreviewFields([]);
    try {
      const [tRes, cRes, fRes] = await Promise.all([
        api.get(`/api/tickets/${ticketId}`),
        api.get(`/api/tickets/${ticketId}/comments`),
        api.get(`/api/tickets/${ticketId}/custom-fields`),
      ]);
      setPreviewTicket(tRes.data);
      setPreviewComments(cRes.data?.comments || []);
      setPreviewFields(fRes.data?.fields || []);
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load ticket');
      setPreviewTicketId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadExpandedReportItems = async (reportId: string) => {
    if (expandedReportItems[reportId]) return;
    setExpandingReportId(reportId);
    try {
      const res = await api.get(`/api/ticket-audits/reports/${reportId}`);
      setExpandedReportItems((prev) => ({ ...prev, [reportId]: res.data.items || [] }));
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load report items');
    } finally {
      setExpandingReportId(null);
    }
  };

  const fetchReportDetail = async (reportId: string) => {
    const res = await api.get(`/api/ticket-audits/reports/${reportId}`);
    return res.data as { report: any; items: any[] };
  };

  const reportExportFilename = (report: any, ext: string) => {
    const agentName = `${report.agent_first_name || ''}_${report.agent_last_name || ''}`
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9_-]/g, '');
    const period = formatPeriodDate(report.period_start);
    return `ticket-audit-${agentName || 'report'}-${period}.${ext}`;
  };

  const exportReportExcel = async (reportId: string) => {
    setExportBusy(`${reportId}:xlsx`);
    try {
      const { report, items } = await fetchReportDetail(reportId);
      const wb = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.json_to_sheet([
        {
          agent: `${report.agent_first_name || ''} ${report.agent_last_name || ''}`.trim(),
          period_start: formatPeriodDate(report.period_start),
          period_end: formatPeriodDate(report.period_end),
          auditor: `${report.auditor_first_name || ''} ${report.auditor_last_name || ''}`.trim(),
          created_at: new Date(report.created_at).toLocaleString(),
          summary_notes: report.summary_notes || '',
        },
      ]);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      const itemsSheet = XLSX.utils.json_to_sheet(
        (items || []).map((it: any) => ({
          ticket_number: it.ticket_number,
          subject: it.subject,
          status: it.status,
          strengths: it.strengths,
          improvements: it.improvements,
          score: it.score,
          feedback_rating: it.feedback_rating,
          feedback_comment: it.feedback_comment,
        }))
      );
      XLSX.utils.book_append_sheet(wb, itemsSheet, 'Tickets');
      XLSX.writeFile(wb, reportExportFilename(report, 'xlsx'));
      message.success('Excel exported');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Export failed');
    } finally {
      setExportBusy(null);
    }
  };

  const exportReportPdf = async (reportId: string) => {
    setExportBusy(`${reportId}:pdf`);
    try {
      const { report, items } = await fetchReportDetail(reportId);
      const doc = new jsPDF();
      const agentName = `${report.agent_first_name || ''} ${report.agent_last_name || ''}`.trim();
      const auditorName = `${report.auditor_first_name || ''} ${report.auditor_last_name || ''}`.trim();
      doc.setFontSize(14);
      doc.text('Ticket Audit Report', 14, 16);
      doc.setFontSize(10);
      doc.text(`Agent: ${agentName || '—'}`, 14, 26);
      doc.text(
        `Period: ${formatPeriodDate(report.period_start)} → ${formatPeriodDate(report.period_end)}`,
        14,
        32
      );
      doc.text(`Auditor: ${auditorName || '—'}`, 14, 38);
      doc.text(`Created: ${new Date(report.created_at).toLocaleString()}`, 14, 44);
      let tableStartY = 52;
      if (report.summary_notes) {
        const split = doc.splitTextToSize(`Notes: ${report.summary_notes}`, 180);
        doc.text(split, 14, 50);
        tableStartY = 50 + split.length * 6;
      }
      autoTable(doc, {
        startY: tableStartY,
        head: [['Ticket', 'Subject', 'Strengths', 'Improvements', 'Score']],
        body: (items || []).map((it: any) => [
          `#${it.ticket_number}`,
          it.subject || '—',
          it.strengths || '—',
          it.improvements || '—',
          it.score != null ? `${it.score}/5` : '—',
        ]),
        styles: { fontSize: 8, overflow: 'linebreak' },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 35 },
          2: { cellWidth: 45 },
          3: { cellWidth: 45 },
          4: { cellWidth: 18 },
        },
      });
      doc.save(reportExportFilename(report, 'pdf'));
      message.success('PDF exported');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Export failed');
    } finally {
      setExportBusy(null);
    }
  };

  const downloadAnalyticsExcel = () => {
    if (!analyticsData) return;
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(
      (analyticsData.by_agent || []).map((r) => ({
        agent: `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim(),
        agent_id: r.agent_id,
        avg_score: r.avg_score,
        item_count: r.item_count,
        report_count: r.report_count,
      }))
    );
    XLSX.utils.book_append_sheet(wb, ws1, 'By agent');
    const ws2 = XLSX.utils.json_to_sheet(analyticsData.by_month || []);
    XLSX.utils.book_append_sheet(wb, ws2, 'By month');
    const fn = `ticket-audit-analytics-${analyticsFrom}_to_${analyticsTo}.xlsx`;
    XLSX.writeFile(wb, fn);
    message.success('Analytics Excel saved');
  };

  const downloadAnalyticsPdf = () => {
    if (!analyticsData) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text('Ticket audit analytics', 14, 16);
    doc.setFontSize(10);
    doc.text(`Range: ${analyticsFrom} to ${analyticsTo}`, 14, 24);
    autoTable(doc, {
      startY: 30,
      head: [['Agent', 'Avg score', 'Items', 'Reports']],
      body: (analyticsData.by_agent || []).map((r) => [
        `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim(),
        r.avg_score != null ? String(r.avg_score) : '—',
        String(r.item_count),
        String(r.report_count),
      ]),
    });
    const yAfter = (doc as any).lastAutoTable?.finalY || 40;
    autoTable(doc, {
      startY: yAfter + 10,
      head: [['Month', 'Reports', 'Avg score']],
      body: (analyticsData.by_month || []).map((r) => [
        r.month_key,
        String(r.report_count),
        r.avg_score != null ? String(r.avg_score) : '—',
      ]),
    });
    doc.save(`ticket-audit-analytics-${analyticsFrom}_to_${analyticsTo}.pdf`);
    message.success('PDF saved');
  };

  const feedbackBlock = (t: SampleTicket | any) => {
    if (!t.feedback_has_row) {
      return <Text type="secondary">No customer feedback record for this ticket.</Text>;
    }
    if (!t.feedback_is_submitted) {
      return <Text type="warning">Feedback was requested but not submitted yet.</Text>;
    }
    return (
      <Space direction="vertical" size={0}>
        <div>
          <Text type="secondary">Customer rating: </Text>
          {t.feedback_rating != null ? (
            <>
              <Rate disabled allowHalf={false} value={t.feedback_rating} count={5} />
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {t.feedback_rating}/5
              </Tag>
            </>
          ) : (
            <Text type="secondary">Submitted (no numeric rating)</Text>
          )}
        </div>
        {t.feedback_comment ? (
          <Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 3, expandable: true }}>
            {t.feedback_comment}
          </Paragraph>
        ) : null}
        {t.feedback_submitted_at && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Submitted {new Date(t.feedback_submitted_at).toLocaleString()}
          </Text>
        )}
      </Space>
    );
  };

  const feedbackSummary = (t: SampleTicket) => {
    if (!t.feedback_has_row) return 'No feedback';
    if (!t.feedback_is_submitted) return 'Pending';
    return t.feedback_rating != null ? `${t.feedback_rating}/5` : 'Submitted';
  };

  const agentSelectOptions = useMemo(() => {
    return auditableUsers.map((u) => {
      let hint = 'No audit yet';
      if (u.last_audit_at) {
        hint = `Last audit ${new Date(u.last_audit_at).toLocaleDateString()}`;
        if (u.last_period_start && u.last_period_end) {
          hint += ` (${formatPeriodDate(u.last_period_start)} → ${formatPeriodDate(u.last_period_end)})`;
        }
      }
      return {
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.role}) — ${hint}`,
      };
    });
  }, [auditableUsers]);

  const reportsAgentOptions = useMemo(() => {
    const byId = new Map<string, string>();
    auditableUsers.forEach((u) => {
      byId.set(u.id, `${u.first_name} ${u.last_name}`.trim());
    });
    (reportsData?.reports || []).forEach((r: any) => {
      if (!r.agent_id) return;
      const name = `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim();
      if (name && !byId.has(r.agent_id)) byId.set(r.agent_id, name);
    });
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [auditableUsers, reportsData?.reports]);

  const candidateColumns = useMemo(
    () => [
      {
        title: 'Select',
        key: 'select',
        width: 70,
        render: (_: any, t: SampleTicket) => (
          <Checkbox
            checked={selectedTicketIds.includes(t.id)}
            disabled={
              !selectedTicketIds.includes(t.id) && selectedTicketIds.length >= selectionSize
            }
            onChange={(e) => toggleTicketSelection(t.id, e.target.checked)}
          />
        ),
      },
      {
        title: 'Ticket',
        key: 'ticket',
        render: (_: any, t: SampleTicket) => (
          <Space direction="vertical" size={0}>
            <Text strong>#{t.ticket_number}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t.subject}
            </Text>
          </Space>
        ),
      },
      { title: 'Business', dataIndex: 'business_name', key: 'business', render: (v: string) => v || '—' },
      { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
      {
        title: 'Last updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 160,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
      },
      {
        title: 'Feedback',
        key: 'feedback',
        width: 100,
        render: (_: any, t: SampleTicket) => feedbackSummary(t),
      },
      {
        title: '',
        key: 'preview',
        width: 90,
        render: (_: any, t: SampleTicket) => (
          <Button type="link" size="small" onClick={() => openTicketPreview(t.id)}>
            Preview
          </Button>
        ),
      },
    ],
    [selectedTicketIds, selectionSize]
  );

  const historyColumns = useMemo(
    () => [
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (v: string) => new Date(v).toLocaleString(),
      },
      {
        title: 'Agent',
        key: 'agent',
        render: (_: any, r: any) =>
          `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim() || '—',
      },
      {
        title: 'Period',
        key: 'period',
        render: (_: any, r: any) =>
          `${formatPeriodDate(r.period_start)} → ${formatPeriodDate(r.period_end)}`,
      },
      {
        title: 'Auditor',
        key: 'auditor',
        render: (_: any, r: any) =>
          `${r.auditor_first_name || ''} ${r.auditor_last_name || ''}`.trim() || '—',
      },
      { title: 'Tickets', dataIndex: 'item_count', key: 'item_count', width: 80 },
      {
        title: 'Avg score',
        dataIndex: 'avg_score',
        key: 'avg_score',
        width: 90,
        render: (v: string | number | null) => (v != null ? `${v}/5` : '—'),
      },
      {
        title: '',
        key: 'act',
        width: 220,
        render: (_: any, r: any) => (
          <Space size={0} wrap>
            <Button type="link" size="small" onClick={() => openDetail(r.id)}>
              View
            </Button>
            <Button
              type="link"
              size="small"
              icon={<ExportOutlined />}
              loading={exportBusy === `${r.id}:xlsx`}
              onClick={() => exportReportExcel(r.id)}
            >
              Excel
            </Button>
            <Button
              type="link"
              size="small"
              icon={<FilePdfOutlined />}
              loading={exportBusy === `${r.id}:pdf`}
              onClick={() => exportReportPdf(r.id)}
            >
              PDF
            </Button>
          </Space>
        ),
      },
    ],
    [openDetail, exportBusy]
  );

  const fieldValueDisplay = (v: unknown) => {
    if (v == null) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const reportsErrorMessage =
    (reportsQueryError as any)?.response?.data?.error || 'Failed to load audit reports';

  const tabItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      children: (
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap align="center">
              <Text strong>Date range</Text>
              <DatePicker.RangePicker
                value={analyticsRange || undefined}
                onChange={(r) => setAnalyticsRange(r as [Dayjs, Dayjs] | null)}
              />
              <Button icon={<ExportOutlined />} onClick={downloadAnalyticsExcel} disabled={!analyticsData}>
                Excel
              </Button>
              <Button icon={<FilePdfOutlined />} onClick={downloadAnalyticsPdf} disabled={!analyticsData}>
                PDF
              </Button>
            </Space>
            {analyticsLoading ? (
              <Spin />
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Text strong>Average score by agent</Text>
                  <div style={{ width: '100%', height: 320, marginTop: 8 }}>
                    <ResponsiveContainer>
                      <BarChart data={barChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="avgScore" name="Avg score" fill="#1677ff" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <Text strong>Activity by month</Text>
                  <div style={{ width: '100%', height: 320, marginTop: 8 }}>
                    <ResponsiveContainer>
                      <LineChart data={lineChartData} margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 5]} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="reports" name="Reports" stroke="#52c41a" />
                        <Line yAxisId="right" type="monotone" dataKey="avgScore" name="Avg score" stroke="#1677ff" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Col>
              </Row>
            )}
          </Space>
        </Card>
      ),
    },
    {
      key: 'new-audit',
      label: 'New audit',
      children: (
        <Card>
          {configLoading ? (
            <Text type="secondary">Loading configuration…</Text>
          ) : (
            <Text type="secondary">
              Cadence: <Tag>{frequency}</Tag> · Pool: <Tag>{poolSize}</Tag> recent tickets · Audit:{' '}
              <Tag>{selectionSize}</Tag> per session · Roles:{' '}
              <Tag>{(config?.target_roles || []).join(', ') || 'agent, developer'}</Tag>
              <Button type="link" size="small" onClick={applyFrequencyDefaults}>
                Reset period to match cadence
              </Button>
            </Text>
          )}
          <Divider style={{ margin: '12px 0' }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Text strong>Agent</Text>
              <Select
                showSearch
                allowClear
                placeholder={auditableLoading ? 'Loading…' : 'Select agent'}
                style={{ width: '100%', marginTop: 8 }}
                optionFilterProp="label"
                loading={auditableLoading}
                value={agentId}
                onChange={(v) => setAgentId(v)}
                options={agentSelectOptions}
              />
            </Col>
            <Col xs={24} md={10}>
              <Text strong>Audit period</Text>
              <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
                Weekly = previous ISO week; bi-weekly = last 14 days; monthly = previous calendar month when you
                reset from Settings cadence.
              </Paragraph>
              <DatePicker.RangePicker
                style={{ width: '100%', marginTop: 8 }}
                value={period || undefined}
                onChange={(r) => setPeriod(r as [Dayjs, Dayjs] | null)}
              />
            </Col>
            <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button icon={<ReloadOutlined />} loading={loadingCandidates} onClick={loadCandidates}>
                Load last {poolSize} tickets
              </Button>
            </Col>
          </Row>

          {candidates.length > 0 && (
            <>
              <Divider />
              <Space style={{ marginBottom: 12 }}>
                <Text strong>
                  Select {selectionSize} tickets to audit ({selectedTicketIds.length} of {selectionSize}{' '}
                  selected)
                </Text>
              </Space>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={candidates}
                columns={candidateColumns}
                style={{ marginBottom: 16 }}
              />
            </>
          )}

          {selectedTickets.length > 0 && (
            <>
              <Divider />
              <Text strong>Summary notes (optional)</Text>
              <TextArea
                style={{ marginTop: 8, marginBottom: 16 }}
                rows={2}
                value={summaryNotes}
                onChange={(e) => setSummaryNotes(e.target.value)}
                placeholder="Overall notes for this audit session"
              />
              {selectedTickets.map((t) => (
                <Card
                  key={t.id}
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <Space>
                      <span>
                        #{t.ticket_number} — {t.subject}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<FileSearchOutlined />}
                        onClick={() => openTicketPreview(t.id)}
                      >
                        Preview ticket
                      </Button>
                    </Space>
                  }
                >
                  <Row gutter={16}>
                    <Col xs={24} md={10}>
                      <Text type="secondary">Business: {t.business_name || '—'}</Text>
                      <br />
                      <Text type="secondary">Status: {t.status}</Text>
                      <br />
                      <Text type="secondary">
                        Last updated:{' '}
                        {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}
                      </Text>
                      <Divider orientation="left" plain style={{ fontSize: 12 }}>
                        Customer feedback
                      </Divider>
                      {feedbackBlock(t)}
                      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                        Use the above when scoring; your grade is recorded only in this audit.
                      </Text>
                    </Col>
                    <Col xs={24} md={14}>
                      <Form layout="vertical">
                        <Form.Item label="What went well">
                          <TextArea
                            rows={2}
                            value={grades[t.id]?.strengths || ''}
                            onChange={(e) =>
                              setGrades((g) => ({
                                ...g,
                                [t.id]: {
                                  ...g[t.id],
                                  strengths: e.target.value,
                                  improvements: g[t.id]?.improvements || '',
                                  score: g[t.id]?.score,
                                },
                              }))
                            }
                          />
                        </Form.Item>
                        <Form.Item label="Needs improvement">
                          <TextArea
                            rows={2}
                            value={grades[t.id]?.improvements || ''}
                            onChange={(e) =>
                              setGrades((g) => ({
                                ...g,
                                [t.id]: {
                                  strengths: g[t.id]?.strengths || '',
                                  improvements: e.target.value,
                                  score: g[t.id]?.score,
                                },
                              }))
                            }
                          />
                        </Form.Item>
                        <Form.Item label="Supervisor score (1–5, optional)">
                          <Rate
                            allowClear
                            count={5}
                            value={grades[t.id]?.score}
                            onChange={(v) =>
                              setGrades((g) => ({
                                ...g,
                                [t.id]: {
                                  strengths: g[t.id]?.strengths || '',
                                  improvements: g[t.id]?.improvements || '',
                                  score: v,
                                },
                              }))
                            }
                          />
                        </Form.Item>
                      </Form>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Affix offsetBottom={16}>
                <div
                  style={{ background: '#fff', padding: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}
                >
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={saveReport}
                    size="large"
                    disabled={selectedTicketIds.length !== selectionSize}
                  >
                    Save audit report ({selectedTicketIds.length}/{selectionSize})
                  </Button>
                </div>
              </Affix>
            </>
          )}
        </Card>
      ),
    },
    {
      key: 'audit-reports',
      label: 'Audit Reports',
      children: (
        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              showSearch
              placeholder="Filter by agent"
              style={{ width: 260 }}
              value={reportsAgentFilter}
              loading={auditableLoading}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(v) => {
                setReportsAgentFilter(v);
                setReportsPage(1);
              }}
              options={reportsAgentOptions}
            />
            <DatePicker.RangePicker
              value={reportsDateRange || undefined}
              onChange={(r) => {
                setReportsDateRange(r as [Dayjs, Dayjs] | null);
                setReportsPage(1);
              }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Date filter matches the audit period (not when the report was saved).
            </Text>
            <Button icon={<ReloadOutlined />} onClick={() => refetchReports()}>
              Refresh
            </Button>
          </Space>
          {reportsError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={reportsErrorMessage}
              action={
                <Button size="small" onClick={() => refetchReports()}>
                  Retry
                </Button>
              }
            />
          ) : null}
          <Table
            rowKey="id"
            loading={reportsLoading}
            dataSource={reportsData?.reports || []}
            columns={historyColumns}
            locale={{ emptyText: 'No saved audits yet' }}
            rowClassName={(r) => (r.id === highlightReportId ? 'ticket-audit-row-highlight' : '')}
            expandable={{
              onExpand: (expanded, record) => {
                if (expanded) loadExpandedReportItems(record.id);
              },
              expandedRowRender: (record) => {
                const items = expandedReportItems[record.id];
                if (!items && expandingReportId === record.id) return <Spin size="small" />;
                if (!items?.length) return <Text type="secondary">No items</Text>;
                return (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {items.map((it: any) => (
                      <Card key={it.id} size="small" type="inner" title={`#${it.ticket_number} — ${it.subject || ''}`}>
                        <Text strong>Strengths: </Text>
                        <Paragraph style={{ marginBottom: 4 }}>{it.strengths || '—'}</Paragraph>
                        <Text strong>Improvements: </Text>
                        <Paragraph style={{ marginBottom: 4 }}>{it.improvements || '—'}</Paragraph>
                        <Text strong>Score: </Text> {it.score != null ? `${it.score}/5` : '—'}
                      </Card>
                    ))}
                  </Space>
                );
              },
            }}
            pagination={{
              current: reportsPage,
              pageSize: reportsPageSize,
              total: reportsData?.total || 0,
              showSizeChanger: false,
              onChange: (p) => setReportsPage(p),
            }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <style>{`.ticket-audit-row-highlight { background: #f6ffed !important; }`}</style>
      <Title level={2}>
        <FileSearchOutlined style={{ marginRight: 12 }} />
        Ticket Audit
      </Title>
      <Paragraph type="secondary">
        Load an agent&apos;s most recent tickets for the audit period, select which to grade, and review saved
        audits anytime. Defaults come from Settings → Ticket audit.
      </Paragraph>

      {saveBannerVisible && lastSavedReportId && (
        <Alert
          style={{ marginBottom: 16 }}
          type="success"
          showIcon
          closable
          onClose={() => setSaveBannerVisible(false)}
          message={
            <span>
              Audit saved — review your feedback in the detail view or{' '}
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openDetail(lastSavedReportId)}>
                reopen this report
              </Button>
              .
            </span>
          }
        />
      )}

      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />

      <Modal
        title="Audit report detail"
        open={!!detailReport}
        onCancel={() => setDetailReport(null)}
        footer={null}
        width={800}
      >
        {detailReport && (
          <>
            <Paragraph>
              <Text strong>Agent: </Text>
              {detailReport.report.agent_first_name} {detailReport.report.agent_last_name}
              <br />
              <Text strong>Period: </Text>
              {formatPeriodDate(detailReport.report.period_start)} →{' '}
              {formatPeriodDate(detailReport.report.period_end)}
              <br />
              <Text strong>Auditor: </Text>
              {detailReport.report.auditor_first_name} {detailReport.report.auditor_last_name}
            </Paragraph>
            {detailReport.report.summary_notes ? (
              <Alert type="info" message={detailReport.report.summary_notes} style={{ marginBottom: 16 }} />
            ) : null}
            {(detailReport.items || []).map((it: any) => (
              <Card
                key={it.id}
                size="small"
                style={{ marginBottom: 12 }}
                title={
                  <Space>
                    <span>
                      #{it.ticket_number} — {it.subject || ''}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(`/workspace/${it.ticket_id}`)}
                    >
                      Open in workspace
                    </Button>
                  </Space>
                }
              >
                <Paragraph style={{ marginBottom: 8 }}>{feedbackBlock(it)}</Paragraph>
                <Text strong>Strengths: </Text>
                <Paragraph>{it.strengths || '—'}</Paragraph>
                <Text strong>Improvements: </Text>
                <Paragraph>{it.improvements || '—'}</Paragraph>
                <Text strong>Score: </Text> {it.score != null ? `${it.score}/5` : '—'}
              </Card>
            ))}
          </>
        )}
      </Modal>

      <Modal
        title={previewTicket ? `Ticket ${previewTicket.ticket_number}` : 'Ticket preview'}
        open={!!previewTicketId}
        onCancel={() => {
          setPreviewTicketId(null);
          setPreviewTicket(null);
        }}
        footer={null}
        width={720}
      >
        {previewLoading ? (
          <Spin />
        ) : previewTicket ? (
          <Tabs
            items={[
              {
                key: 'summary',
                label: 'Summary',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Subject: </Text>
                      {previewTicket.subject}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Status: </Text> {previewTicket.status}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Priority: </Text> {previewTicket.priority || '—'}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Category: </Text> {previewTicket.category_name || '—'}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Created: </Text> {new Date(previewTicket.created_at).toLocaleString()}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Resolved: </Text>{' '}
                      {previewTicket.resolved_at
                        ? new Date(previewTicket.resolved_at).toLocaleString()
                        : '—'}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Time to solve: </Text>
                      {formatResolveDuration(previewTicket.created_at, previewTicket.resolved_at)}
                    </Paragraph>
                    <Paragraph>
                      <Text strong>Description</Text>
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                        {previewTicket.description || '—'}
                      </div>
                    </Paragraph>
                    <Button
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(`/workspace/${previewTicket.id}`)}
                    >
                      Open in workspace
                    </Button>
                  </Space>
                ),
              },
              {
                key: 'comments',
                label: `Comments (${previewComments.length})`,
                children: (
                  <div style={{ maxHeight: 360, overflow: 'auto' }}>
                    {previewComments.length === 0 ? (
                      <Text type="secondary">No comments</Text>
                    ) : (
                      previewComments.map((c: any) => (
                        <Card key={c.id} size="small" style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(c.created_at).toLocaleString()}
                            {c.is_internal ? <Tag style={{ marginLeft: 8 }}>Internal</Tag> : null}
                          </Text>
                          <div
                            style={{
                              marginTop: 8,
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              color: '#262626',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                            dangerouslySetInnerHTML={{
                              __html: String(
                                c.content ||
                                  c.comment ||
                                  '<span style="color: #bfbfbf; font-style: italic;">No content</span>'
                              ),
                            }}
                          />
                        </Card>
                      ))
                    )}
                  </div>
                ),
              },
              {
                key: 'fields',
                label: 'Custom fields',
                children: (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={previewFields}
                    columns={[
                      { title: 'Field', dataIndex: 'label', key: 'label' },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        key: 'value',
                        render: (_: any, row: any) => fieldValueDisplay(row.value),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default TicketAudit;
